import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
const SIDEBAR_WIDTH = 240;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  external?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Runs', path: '/runs', icon: <PlayArrowIcon /> },
  { label: 'Robots', path: '/robots', icon: <SmartToyOutlinedIcon /> },
  { label: 'Docs', path: 'https://docs.canard.cloud', icon: <DescriptionIcon />, external: true },
  { label: 'Settings', path: '/settings', icon: <SettingsIcon /> },
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => {
    if (path === '/runs') {
      return location.pathname === '/' || location.pathname.startsWith('/runs');
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        height: '100vh',
        borderRight: '1px solid rgba(255, 255, 255, 0.12)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#000000',
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        }}
      >
        <img
          src="/exo-logo.png"
          alt="Exo"
          style={{ height: 40, width: 'auto' }}
        />
      </Box>

      {/* New Run button */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/runs/new')}
          sx={{
            backgroundColor: '#EF8997',
            '&:hover': { backgroundColor: '#e5707f' },
            fontWeight: 600,
            fontSize: 13,
            py: 1,
          }}
        >
          New Run
        </Button>
      </Box>

      {/* Navigation */}
      <List sx={{ flex: 1, pt: 1 }}>
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <ListItemButton
              key={item.path}
              {...(item.external
                ? { component: 'a', href: item.path, target: '_blank', rel: 'noopener noreferrer' }
                : { onClick: () => navigate(item.path) }
              )}
              disableRipple
              sx={{
                mx: 1,
                mb: 0.5,
                color: active ? '#EF8997' : 'rgba(255, 255, 255, 0.7)',
                backgroundColor: active ? 'rgba(239, 137, 151, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: active
                    ? 'rgba(239, 137, 151, 0.12)'
                    : 'rgba(255, 255, 255, 0.04)',
                  color: active ? '#EF8997' : '#ffffff',
                },
                transition: 'all 0.15s',
              }}
            >
              <ListItemIcon
                sx={{
                  color: 'inherit',
                  minWidth: 40,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: active ? 600 : 500,
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid rgba(255, 255, 255, 0.12)',
          fontSize: 11,
          color: 'rgba(255, 255, 255, 0.4)',
          textAlign: 'center',
        }}
      >
        Canard v0.1.0
      </Box>
    </Box>
  );
};

export default Sidebar;
export { SIDEBAR_WIDTH };
